import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";
import { RiLogoutBoxRLine, RiSearchLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";
import InitialsAvatar from "../kit/InitialsAvatar";
import ThemeSwitch from "../kit/ThemeSwitch";
import BrandLogo from "../kit/BrandLogo";
import { NAV_SECTIONS, groupBySection } from "./navSections";

export const SIDEBAR_WIDTH = 264;
export const SIDEBAR_COLLAPSED_WIDTH = 76;

const oneLine = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

// A menu row, open (Admin · Nav Item 10592:82): 38 px; the active row light blue with a
// 3 × 20 blue bar on its left edge; the Inbox count as a red badge.
const NavRow = ({ item, active, onNavigate }) => {
  const { Icon } = item;
  return (
    <ButtonBase
      component={RouterLink}
      to={item.path}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      sx={{
        position: "relative",
        height: 38,
        px: "12px",
        gap: "8px",
        justifyContent: "flex-start",
        borderRadius: "8px",
        typography: "bodySmall",
        color: active ? tokens.brand.text : tokens.text.primary,
        bgcolor: active ? tokens.brand.subtle : "transparent",
        "&:hover": { bgcolor: active ? tokens.brand.subtle : tokens.hover },
        ...focusRing,
      }}
    >
      {active && (
        <Box aria-hidden sx={{ position: "absolute", left: 0, top: "calc(50% - 10px)", width: 3, height: 20, bgcolor: tokens.brand.primary }} />
      )}
      <Icon size={18} />
      <Box component="span" sx={{ flex: 1, minWidth: 0, textAlign: "left", ...oneLine }}>
        {item.text}
      </Box>
      {item.badge && (
        <Box
          component="span"
          sx={{
            px: "7px",
            py: "1px",
            borderRadius: "999px",
            bgcolor: tokens.error.main,
            color: tokens.text.inverse,
            fontSize: 12,
            lineHeight: "16px",
            fontWeight: 500,
            letterSpacing: "0.2px",
          }}
        >
          {item.badge}
        </Box>
      )}
    </ButtonBase>
  );
};

// A menu row, collapsed (Admin · Nav Rail Item 10592:107): a 44 px icon button with its name
// in a tooltip; the Inbox count becomes a red dot.
const RailButton = ({ label, Icon, to, onClick, active = false, dot = false }) => (
  <Tooltip title={label} placement="right">
    <ButtonBase
      component={to ? RouterLink : "button"}
      to={to}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      sx={{
        position: "relative",
        width: 44,
        height: 44,
        borderRadius: "10px",
        color: active ? tokens.brand.text : tokens.icon.default,
        bgcolor: active ? tokens.brand.subtle : "transparent",
        "&:hover": { bgcolor: active ? tokens.brand.subtle : tokens.hover },
        ...focusRing,
      }}
    >
      <Icon size={18} />
      {dot && (
        <Box aria-hidden sx={{ position: "absolute", top: 7, right: 7, width: 8, height: 8, borderRadius: "999px", bgcolor: tokens.error.main }} />
      )}
    </ButtonBase>
  </Tooltip>
);

// Admin · Sidebar (047 D10) around 044 D1's menu: the logo mark, a Search box that opens
// the search panel, the six groups, the Light | Dark switch (D107), and the user card with
// sign-out. Collapsed, the rows become icon buttons, the switch hides (the top bar's moon still
// switches) and the user card shows only its avatar.
const Sidebar = ({ items, activePath, collapsed = false, user, onNavigate, onSearch, onLogout }) => {
  const groups = groupBySection(items);
  const sections = NAV_SECTIONS.filter((s) => groups[s.key].length > 0);
  const name = user?.name || user?.displayName || user?.email || "Staff";
  const colorKey = user?.uid || name;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: tokens.surface.default }}>
      <Box
        sx={{
          height: collapsed ? 60 : 72,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          px: collapsed ? 0 : "20px",
        }}
      >
        <BrandLogo alt="121 Islam" sx={{ width: collapsed ? 49 : 74, height: collapsed ? 24 : 36, objectFit: "contain" }} />
      </Box>

      {!collapsed && (
        <Box sx={{ px: "12px", py: "4px", flexShrink: 0 }}>
          <ButtonBase
            onClick={onSearch}
            sx={{
              width: "100%",
              height: 40,
              gap: "9px",
              px: "12px",
              justifyContent: "flex-start",
              borderRadius: "10px",
              border: `1px solid ${tokens.border.strong}`,
              bgcolor: tokens.surface.default,
              color: tokens.text.tertiary,
              typography: "bodySmall",
              ...focusRing,
            }}
          >
            <RiSearchLine size={20} color={tokens.icon.secondary} />
            Search…
          </ButtonBase>
        </Box>
      )}

      <Box component="nav" aria-label="Main menu" sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", px: "16px", pb: "12px" }}>
        {collapsed && (
          <Box sx={{ pb: "4px" }}>
            <RailButton label="Search" Icon={RiSearchLine} onClick={onSearch} />
          </Box>
        )}
        {sections.map((section, index) => (
          <Box key={section.key}>
            {collapsed ? (
              index > 0 && (
                <Box aria-hidden sx={{ height: 13, display: "flex", alignItems: "center" }}>
                  <Box sx={{ width: 44, height: "1px", bgcolor: tokens.divider }} />
                </Box>
              )
            ) : (
              <Typography variant="labelMedium" component="div" sx={{ pt: "16px", pb: "6px", pl: "11px", color: tokens.text.tertiary }}>
                {section.caption}
              </Typography>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: collapsed ? "4px" : "2px" }}>
              {groups[section.key].map((item) =>
                collapsed ? (
                  <RailButton
                    key={item.path}
                    label={item.badge ? `${item.text} (${item.badge})` : item.text}
                    Icon={item.Icon}
                    to={item.path}
                    onClick={onNavigate}
                    active={item.path === activePath}
                    dot={!!item.badge}
                  />
                ) : (
                  <NavRow key={item.path} item={item} active={item.path === activePath} onNavigate={onNavigate} />
                )
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {!collapsed && (
        <Box sx={{ px: "12px", pb: "12px", flexShrink: 0 }}>
          <ThemeSwitch />
        </Box>
      )}

      {collapsed ? (
        <Box sx={{ height: 56, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Tooltip title={name} placement="right">
            <Box role="img" sx={{ display: "flex" }}>
              <InitialsAvatar name={name} colorKey={colorKey} size={32} />
            </Box>
          </Tooltip>
        </Box>
      ) : (
        <Box
          sx={{
            height: 64,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            p: "12px",
            borderTop: `1px solid ${tokens.border.subtle}`,
          }}
        >
          <InitialsAvatar name={name} colorKey={colorKey} size={40} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
              {name}
            </Typography>
            {user?.email && (
              <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
                {user.email}
              </Typography>
            )}
          </Box>
          <Tooltip title="Sign out">
            <IconButton
              aria-label="Sign out"
              onClick={onLogout}
              sx={{ p: "6px", borderRadius: "7px", color: tokens.icon.secondary, "&:hover": { bgcolor: tokens.hover } }}
            >
              <RiLogoutBoxRLine size={17} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
